import React, { useState } from 'react';

// Modern color scheme
const colors = {
  primary: '#2563eb',
  primaryHover: '#1d4ed8',
  secondary: '#64748b',
  light: '#f8fafc',
  lighter: '#f1f5f9',
  dark: '#1e293b',
  border: '#e2e8f0',
  text: '#334155',
  textMuted: '#64748b'
};

// CSS Styles
const styles = {
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: colors.light,
    borderTop: `1px solid ${colors.border}`,
    borderRadius: '0 0 12px 12px'
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  pageInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    fontSize: '0.875rem',
    color: colors.text
  },
  pageSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  select: {
    padding: '0.375rem 0.75rem',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    color: colors.text,
    backgroundColor: '#fff',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
  },
  btn: {
    display: 'inline-block',
    fontWeight: 500,
    textAlign: 'center',
    verticalAlign: 'middle',
    userSelect: 'none',
    border: '1px solid transparent',
    padding: '0.375rem 0.75rem',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    borderRadius: '6px',
    transition: 'all 0.15s ease-in-out',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  btnDefault: {
    color: colors.text,
    backgroundColor: '#fff',
    borderColor: colors.border
  },
  btnDefaultHover: {
    backgroundColor: colors.lighter,
    borderColor: colors.secondary
  },
  btnPrimary: {
    color: '#fff',
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  btnPrimaryHover: {
    backgroundColor: colors.primaryHover,
    borderColor: colors.primaryHover
  },
  disabled: {
    color: colors.textMuted,
    pointerEvents: 'none',
    cursor: 'auto',
    backgroundColor: '#fff',
    borderColor: colors.border,
    opacity: 0.65
  },
  active: {
    zIndex: 1,
    color: '#fff',
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  pageNumbers: {
    display: 'flex',
    gap: '0.25rem'
  },
  pageBtn: {
    minWidth: '2.5rem',
    height: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  badge: {
    display: 'inline-block',
    padding: '0.375em 0.75em',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'baseline',
    borderRadius: '6px',
    backgroundColor: colors.lighter,
    color: colors.text
  }
};

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  itemsPerPage, 
  onItemsPerPageChange,
  totalItems,
  showPageNumbers = true,
  showItemsPerPageSelector = true,
  maxPageButtons = 5
}) => {
  const [hoveredButton, setHoveredButton] = useState(null);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFirstPage = () => {
    onPageChange(1);
  };

  const handleLastPage = () => {
    onPageChange(totalPages);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    onItemsPerPageChange(newItemsPerPage);
    onPageChange(1); // Reset to first page when changing items per page
  };

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    // Adjust start page if we're near the end
    const adjustedStartPage = Math.max(1, endPage - maxPageButtons + 1);

    for (let i = adjustedStartPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const getStartEndItems = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    return { start, end };
  };

  const { start, end } = getStartEndItems();

  return (
    <div style={styles.paginationContainer}>
      {/* Items per page selector */}
      {showItemsPerPageSelector && (
        <div style={styles.pageSelector}>
          <span style={{ color: colors.textMuted }}>Show</span>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={styles.select}
          >
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={300}>300</option>
            <option value={400}>400</option>
            <option value={500}>500</option>
          </select>
          <span style={{ color: colors.textMuted }}>entries</span>
        </div>
      )}

      {/* Page information */}
      <div style={styles.pageInfo}>
        <span style={styles.badge}>
          {totalItems > 0 ? `${start}-${end} of ${totalItems}` : '0 items'}
        </span>
        {totalPages > 0 && (
          <span style={{ color: colors.textMuted }}>
            Page {currentPage} of {totalPages}
          </span>
        )}
      </div>

      {/* Pagination controls */}
      <div style={styles.paginationControls}>
        {/* First and Previous buttons */}
        <button
          onClick={handleFirstPage}
          disabled={currentPage === 1}
          style={{
            ...styles.btn,
            ...styles.btnDefault,
            ...(currentPage === 1 ? styles.disabled : {}),
            ...(hoveredButton === 'first' && currentPage !== 1 ? styles.btnDefaultHover : {})
          }}
          onMouseEnter={() => setHoveredButton('first')}
          onMouseLeave={() => setHoveredButton(null)}
          title="First Page"
        >
          &laquo;&laquo;
        </button>

        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          style={{
            ...styles.btn,
            ...styles.btnDefault,
            ...(currentPage === 1 ? styles.disabled : {}),
            ...(hoveredButton === 'prev' && currentPage !== 1 ? styles.btnDefaultHover : {})
          }}
          onMouseEnter={() => setHoveredButton('prev')}
          onMouseLeave={() => setHoveredButton(null)}
          title="Previous Page"
        >
          &laquo;
        </button>

        {/* Page numbers */}
        {showPageNumbers && totalPages > 1 && (
          <div style={styles.pageNumbers}>
            {getPageNumbers().map(pageNum => (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                style={{
                  ...styles.btn,
                  ...styles.pageBtn,
                  ...(pageNum === currentPage ? styles.btnPrimary : styles.btnDefault),
                  ...(pageNum === currentPage ? {} : (hoveredButton === pageNum ? styles.btnDefaultHover : {}))
                }}
                onMouseEnter={() => setHoveredButton(pageNum)}
                onMouseLeave={() => setHoveredButton(null)}
              >
                {pageNum}
              </button>
            ))}
          </div>
        )}

        {/* Next and Last buttons */}
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            ...styles.btn,
            ...styles.btnDefault,
            ...(currentPage === totalPages || totalPages === 0 ? styles.disabled : {}),
            ...(hoveredButton === 'next' && currentPage !== totalPages ? styles.btnDefaultHover : {})
          }}
          onMouseEnter={() => setHoveredButton('next')}
          onMouseLeave={() => setHoveredButton(null)}
          title="Next Page"
        >
          &raquo;
        </button>

        <button
          onClick={handleLastPage}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            ...styles.btn,
            ...styles.btnDefault,
            ...(currentPage === totalPages || totalPages === 0 ? styles.disabled : {}),
            ...(hoveredButton === 'last' && currentPage !== totalPages ? styles.btnDefaultHover : {})
          }}
          onMouseEnter={() => setHoveredButton('last')}
          onMouseLeave={() => setHoveredButton(null)}
          title="Last Page"
        >
          &raquo;&raquo;
        </button>
      </div>
    </div>
  );
};

export default Pagination;
